class TabBlinker {
    /**
     * 初始化标签页闪烁器
     * @param {Object} options - 配置选项
     * @param {string} [options.blinkText='【通知】'] - 闪烁时显示的文本
     * @param {number} [options.interval=500] - 闪烁间隔时间(毫秒)
     * @param {string} [options.blinkFavicon] - 闪烁时的图标URL
     * @param {number} [options.blinkCount=3] - 闪烁次数(一次闪烁指一明一暗)
     */
    constructor(options = {}) {
        // 默认配置
        this.options = {
            blinkText: '【通知】',
            interval: 500,
            blinkFavicon: null,
            blinkCount: 3, // 默认闪烁3次
            ...options
        };

        // 保存原始状态（使用闭包保存，避免刷新后丢失）
        if (!window.__tabBlinkerOriginalState) {
            window.__tabBlinkerOriginalState = {
                title: document.title,
                favicon: this._getCurrentFavicon()
            };
        }
        this.originalTitle = window.__tabBlinkerOriginalState.title;
        this.originalFavicon = window.__tabBlinkerOriginalState.favicon;

        // 状态变量
        this.isBlinking = false;
        this.blinkTimer = null;
        this.isOriginalState = true;
        this.currentBlinkCount = 0; // 当前闪烁次数计数器

        // 绑定事件
        this._bindVisibilityChange();

        // 页面卸载前确保恢复原始状态
        window.addEventListener('beforeunload', () => {
            this._restoreOriginalState();
        });
    }

    /**
     * 获取当前页面的favicon
     */
    _getCurrentFavicon() {
        const link = document.querySelector('link[rel*="icon"]');
        return link ? link.href : null;
    }

    /**
     * 设置页面favicon
     * @param {string} url - 图标URL
     */
    _setFavicon(url) {
        if (!url) return;

        let link = document.querySelector('link[rel*="icon"]');
        if (!link) {
            link = document.createElement('link');
            link.rel = 'shortcut icon';
            document.head.appendChild(link);
        }
        link.href = url;
    }

    /**
     * 切换标签页显示状态（内部方法）
     */
    _toggleState() {
        // 只有当页面不可见时才执行切换
        if (document.visibilityState !== 'visible') {
            this.isOriginalState = !this.isOriginalState;

            // 切换标题
            document.title = this.isOriginalState
                ? this.originalTitle
                : this.options.blinkText;

            // 切换图标（如果提供了闪烁图标）
            if (this.options.blinkFavicon) {
                this._setFavicon(this.isOriginalState
                    ? this.originalFavicon
                    : this.options.blinkFavicon);
            }

            // 当切换回原始状态时，计数一次完整闪烁
            if (this.isOriginalState) {
                this.currentBlinkCount++;

                // 达到指定闪烁次数后自动停止
                if (this.currentBlinkCount >= this.options.blinkCount) {
                    this.stop();
                }
            }
        } else {
            // 如果页面可见但处于闪烁状态，强制恢复
            if (this.isBlinking) {
                this._restoreOriginalState();
            }
        }
    }

    /**
     * 绑定页面可见性变化事件
     */
    _bindVisibilityChange() {
        // 使用箭头函数确保this指向正确
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // 当页面可见时恢复原始状态
                this._restoreOriginalState();
            } else if (this.isBlinking) {
                // 当页面不可见且处于闪烁状态时，确保从原始状态开始闪烁
                this.isOriginalState = true;
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // 页面显示时也触发一次检查
        window.addEventListener('pageshow', handleVisibilityChange);
    }

    /**
     * 恢复原始状态 - 强化版确保一定能恢复
     */
    _restoreOriginalState() {
        // 强制恢复，不做状态检查
        document.title = this.originalTitle;
        if (this.originalFavicon) {
            this._setFavicon(this.originalFavicon);
        }
        this.isOriginalState = true;
    }

    /**
     * 开始闪烁
     */
    start() {
        // 重置计数器
        this.currentBlinkCount = 0;

        if (!this.isBlinking) {
            this.isBlinking = true;
            // 无论页面是否可见，都启动定时器，但在toggleState中会判断是否执行
            this.blinkTimer = setInterval(() => this._toggleState(), this.options.interval);
        }
    }

    /**
     * 停止闪烁
     */
    stop() {
        if (this.isBlinking) {
            this.isBlinking = false;
            // 清除定时器
            if (this.blinkTimer) {
                clearInterval(this.blinkTimer);
                this.blinkTimer = null;
            }
            // 强制恢复原始状态，无论当前页面是否可见
            this._restoreOriginalState();

            // 额外保险：即使现在不可见，也确保当页面再次可见时恢复
            const checkVisibility = () => {
                if (document.visibilityState === 'visible') {
                    this._restoreOriginalState();
                    document.removeEventListener('visibilitychange', checkVisibility);
                }
            };
            document.addEventListener('visibilitychange', checkVisibility);
        }
    }

    /**
     * 更新闪烁文本
     * @param {string} text - 新的闪烁文本
     */
    updateBlinkText(text) {
        this.options.blinkText = text;
    }

    /**
     * 更新闪烁间隔
     * @param {number} interval - 新的间隔时间(毫秒)
     */
    updateInterval(interval) {
        this.options.interval = interval;

        // 如果正在闪烁，重新启动定时器
        if (this.isBlinking) {
            clearInterval(this.blinkTimer);
            this.blinkTimer = setInterval(() => this._toggleState(), this.options.interval);
        }
    }

    /**
     * 更新闪烁次数
     * @param {number} count - 新的闪烁次数
     */
    updateBlinkCount(count) {
        this.options.blinkCount = count;
    }
}


export default TabBlinker